import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingCart, Menu, X, ChevronDown } from 'lucide-react';
import { SITE, CATEGORIES } from '../mock/mock';
import { useCart } from '../context/CartContext';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [prodMenu, setProdMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { count, setIsOpen } = useCart();

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
      setMobileOpen(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50">
      {/* top yellow strip */}
      <div className="h-1.5 ps-yellow-bg w-full" />
      <nav className="ps-blue-bg text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-11 h-11 rounded-full ps-yellow-bg flex items-center justify-center border-4 border-white shadow-md group-hover:rotate-12 transition-transform duration-300">
                <span className="font-anton text-[13px] text-[#1a1ad6] leading-none">30</span>
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-anton text-xl sm:text-2xl tracking-wide">
                  PETSPORTS<span className="text-[#ffd400]">EQUIP</span>
                </span>
                <span className="text-[10px] tracking-[0.25em] text-white/70 hidden sm:block">.COM</span>
              </div>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              <Link to="/new" className={`font-oswald font-600 uppercase tracking-wide px-4 py-2 rounded transition-colors ${isActive('/new') ? 'bg-white/15 text-[#ffd400]' : 'hover:text-[#ffd400]'}`}>New!</Link>
              <div className="relative" onMouseEnter={() => setProdMenu(true)} onMouseLeave={() => setProdMenu(false)}>
                <Link to="/products" className={`font-oswald font-600 uppercase tracking-wide px-4 py-2 rounded flex items-center gap-1 transition-colors ${location.pathname.startsWith('/products') || location.pathname.startsWith('/category') ? 'bg-white/15 text-[#ffd400]' : 'hover:text-[#ffd400]'}`}>
                  Products <ChevronDown size={16} className={`transition-transform ${prodMenu ? 'rotate-180' : ''}`} />
                </Link>
                {prodMenu && (
                  <div className="absolute top-full left-0 pt-2 w-60">
                    <div className="bg-white rounded-lg shadow-2xl overflow-hidden py-2 border-t-4 border-[#ffd400]">
                      {CATEGORIES.map((c) => (
                        <Link key={c.slug} to={`/category/${c.slug}`} className="flex items-center justify-between px-4 py-2 text-[#1a1ad6] hover:bg-[#f0f0ff] font-oswald uppercase text-sm">
                          {c.name}
                          {c.isNew && <span className="text-[10px] ps-yellow-bg text-black px-1.5 py-0.5 rounded font-bold">NEW</span>}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Link to="/contact" className={`font-oswald font-600 uppercase tracking-wide px-4 py-2 rounded transition-colors ${isActive('/contact') ? 'bg-white/15 text-[#ffd400]' : 'hover:text-[#ffd400]'}`}>Contact</Link>
            </div>

            {/* Right icons */}
            <div className="flex items-center gap-2">
              <button onClick={() => setSearchOpen((s) => !s)} className="p-2 rounded-full hover:bg-white/15 transition-colors" aria-label="Search">
                <Search size={20} />
              </button>
              <button onClick={() => setIsOpen(true)} className="p-2 rounded-full hover:bg-white/15 transition-colors relative" aria-label="Cart">
                <ShoppingCart size={20} />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 ps-yellow-bg text-black text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{count}</span>
                )}
              </button>
              <button onClick={() => setMobileOpen((m) => !m)} className="md:hidden p-2 rounded-full hover:bg-white/15" aria-label="Menu">
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <form onSubmit={submitSearch} className="pb-4 fade-up">
              <div className="flex bg-white rounded-md overflow-hidden shadow-lg">
                <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products..." className="flex-1 px-4 py-3 text-black outline-none" />
                <button type="submit" className="ps-yellow-bg text-black px-6 font-oswald font-600 uppercase btn-press">Go</button>
              </div>
            </form>
          )}
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden ps-blue-bg border-t border-white/10 fade-up">
            <Link to="/new" onClick={() => setMobileOpen(false)} className="block px-6 py-3 font-oswald uppercase border-b border-white/10">New!</Link>
            <Link to="/products" onClick={() => setMobileOpen(false)} className="block px-6 py-3 font-oswald uppercase border-b border-white/10">Products</Link>
            {CATEGORIES.map((c) => (
              <Link key={c.slug} to={`/category/${c.slug}`} onClick={() => setMobileOpen(false)} className="block px-10 py-2 font-oswald uppercase text-sm text-white/80 border-b border-white/5">{c.name}</Link>
            ))}
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="block px-6 py-3 font-oswald uppercase">Contact</Link>
          </div>
        )}
      </nav>
      {/* bottom yellow strip */}
      <div className="h-1.5 ps-yellow-bg w-full" />
    </header>
  );
};

export default Navbar;
